import {
  useWSSnackbar,
  WSActions,
  WSForm,
  WSInfoBox,
  WSInputText,
  WSInputTextarea,
  WSList,
  WSModal,
  WSModalCustomProps,
  WSPill,
  WSText
} from "@wingspanhq/fe-component-library";
import { useCallback } from "react";
import * as Yup from "yup";

import { WSErrorMessage } from "../../../../components/WSErrorMessage/WSErrorMessage";
import { WSQueries } from "../../../../query/WSQuery";
import { useMutationUpdateRequirementDefinition } from "../../../../query/requirementDefinitions/mutation/useMutationUpdateRequirementDefinition";
import { useQueryRequirementDefinition } from "../../../../query/requirementDefinitions/query/useQueryRequirementDefinition";
import { ErrorContextKey } from "../../../../services/platform";
import { getRequirementDefinitionTemplateByParams } from "../../utils/getRequirementDefinitionTemplateByParams";

type Props = {
  requirementDefinitionId: string;
} & WSModalCustomProps<boolean>;

export const EditRequirementDefinitionModal: React.FC<Props> = ({
  requirementDefinitionId,
  onClose
}) => {
  const { openSnackbar } = useWSSnackbar();
  const queryRequirement = useQueryRequirementDefinition(
    requirementDefinitionId
  );

  const [updateRequirement, updateRequirementMeta] =
    useMutationUpdateRequirementDefinition(requirementDefinitionId);

  const handleCancel = useCallback(() => {
    onClose(false);
  }, [onClose]);

  return (
    <WSModal
      shimmer={queryRequirement.isLoading}
      size="M"
      title="Edit requirement details"
      onClose={handleCancel}
    >
      <WSQueries queries={{ queryRequirement }}>
        {({ queryRequirementData }) => {
          const template = getRequirementDefinitionTemplateByParams({
            type: queryRequirementData.requirementType
          });

          const isNameEditable = !!template.isCustomName;
          const hasInstructions = false && !template.instructions; // TODO: remove this when instructions are added

          return (
            <WSForm
              defaultValues={{
                description: queryRequirementData.description,
                name: queryRequirementData.name
              }}
              validationSchema={Yup.object().shape({
                description: Yup.string(),
                name: Yup.string().required("Name is required")
              })}
              onSubmit={async formData => {
                await updateRequirement(formData, {
                  onSuccess: async () => {
                    onClose(true);
                    openSnackbar({
                      message: "Requirement updated successfully",
                      type: "success"
                    });
                  }
                });
              }}
            >
              <WSList gap="XL">
                <WSList gap="XS">
                  <WSList.Horizontal gap="M" wrap="nowrap">
                    <WSText.ParagraphSm>Name *</WSText.ParagraphSm>
                    <WSPill icon="view" text="Public" theme="neutral" />
                  </WSList.Horizontal>
                  <WSText.ParagraphXs color="gray400">
                    The requirement name is visible to you and contractors
                  </WSText.ParagraphXs>
                  <WSForm.Field
                    component={WSInputText}
                    componentProps={{
                      disabled: !isNameEditable,
                      required: true
                    }}
                    name="name"
                    placeholder="Requirement name"
                  />
                </WSList>

                {hasInstructions ? (
                  <WSList gap="XS">
                    <WSList.Horizontal gap="M" wrap="nowrap">
                      <WSText.ParagraphSm>
                        Instructions to contractor
                      </WSText.ParagraphSm>
                      <WSPill icon="view" text="Public" theme="neutral" />
                    </WSList.Horizontal>
                    <WSText.ParagraphXs color="gray400">
                      The instructions are visible to you and contractors
                    </WSText.ParagraphXs>
                    <WSForm.Field
                      component={WSInputTextarea}
                      componentProps={{ required: false }}
                      name="instructions"
                      placeholder="Add optional instructions for this requirement"
                    />
                  </WSList>
                ) : null}

                <WSInfoBox title="Important note">
                  Some fields, such as name and instructions, are visible to
                  contractors. We recommend communicating with your contractors
                  if the name or instructions change significantly and may
                  impact their requirement.
                </WSInfoBox>

                <WSList gap="XS">
                  <WSList.Horizontal gap="M" wrap="nowrap">
                    <WSText.ParagraphSm>
                      Description (optional)
                    </WSText.ParagraphSm>
                    <WSPill icon="lock" text="Private" theme="neutral" />
                  </WSList.Horizontal>
                  <WSText.ParagraphXs color="gray400">
                    The description is visible internally to you and your team
                    only
                  </WSText.ParagraphXs>
                  <WSForm.Field
                    component={WSInputTextarea}
                    componentProps={{ required: false }}
                    name="description"
                    placeholder="Add optional description for this requirement"
                  />
                </WSList>

                <WSErrorMessage
                  contextKey={ErrorContextKey.UpdateRequirement}
                  error={updateRequirementMeta.error}
                />

                <WSActions
                  alignment="fill"
                  buttons={[
                    {
                      kind: "Secondary",
                      label: "Cancel",
                      onClick: handleCancel,
                      type: "button"
                    },
                    {
                      kind: "Primary",
                      label: "Save changes",
                      loading: updateRequirementMeta.isLoading,
                      type: "submit"
                    }
                  ]}
                  mt="L"
                />
              </WSList>
            </WSForm>
          );
        }}
      </WSQueries>
    </WSModal>
  );
};
